import React, { useState, useEffect } from 'react';
import { useTheme } from '../../context/ThemeContext';
import { Settings, Monitor, Bell, Shield, Server, Link2, Megaphone, Moon, Sun, Layout, Palette, Mail, Loader2, CheckCircle, XCircle, Send, Globe, Plus, Trash2, GripVertical, ExternalLink, Bot, ArrowUp, ArrowDown, Eye, EyeOff, Radar, RefreshCw, Play } from 'lucide-react';
import { PlatformIcon } from '../../components/PlatformIcon';
import { toast } from 'sonner';
import { API } from '../../config/apiBase';


const TABS = [
    { key: 'notificações', label: 'Notificações', icon: Bell },
    { key: 'seguranca', label: 'Seguranca', icon: Shield },
    { key: 'sistema', label: 'Sistema', icon: Server },
    { key: 'integracoes', label: 'Integrações', icon: Link2 },
    { key: 'crawler', label: 'Crawler', icon: Radar },
    { key: 'telegram_btns', label: 'Botões Telegram', icon: Bot },
    { key: 'grupos', label: 'Botões & Links', icon: Globe },
    { key: 'comunicados', label: 'Comunicados', icon: Megaphone },
];

export default function SettingsPage() {
    const [activeTab, setActiveTab] = useState('notificações');
    const { isDark, toggleTheme, layoutMode, toggleLayoutMode } = useTheme();

    return (
        <div className="sv-page" data-testid="settings-page">
            <h2 className="text-lg font-bold" style={{ color: 'var(--text)', fontFamily: "var(--font-display)" }}>Configurações</h2>

            {/* Tabs */}
            <div className="sv-tabs" style={{ flexWrap: 'wrap' }}>
                {TABS.map(tab => (
                    <button key={tab.key} className={`sv-tab ${activeTab === tab.key ? 'active' : ''}`} onClick={() => setActiveTab(tab.key)} data-testid={`settings-tab-${tab.key}`}>
                        <tab.icon size={14} className="inline mr-1.5" />{tab.label}
                    </button>
                ))}
            </div>

            {/* Tab Content */}
            <div className="sv-card-system">
                {activeTab === 'notificações' && <NotificationsTab />}
                {activeTab === 'seguranca' && <SecurityTab />}
                {activeTab === 'sistema' && <SystemTab />}
                {activeTab === 'integracoes' && <IntegrationsTab />}
                {activeTab === 'crawler' && <CrawlerConfigTab />}
                {activeTab === 'telegram_btns' && <TelegramButtonsTab />}
                {activeTab === 'grupos' && <PlatformLinksTab />}
                {activeTab === 'comunicados' && <AnnouncementsTab />}
            </div>
        </div>
    );
}

function SettingRow({ label, desc, children }) {
    return (
        <div className="flex items-center justify-between py-4" style={{ borderBottom: '1px solid var(--border)' }}>
            <div>
                <p className="text-sm font-medium" style={{ color: 'var(--text)' }}>{label}</p>
                {desc && <p className="text-xs mt-0.5" style={{ color: 'var(--muted-2)' }}>{desc}</p>}
            </div>
            {children}
        </div>
    );
}

function ToggleSwitch({ checked, onChange }) {
    return (
        <button onClick={onChange} className="w-10 h-6 rounded-full flex items-center transition-colors" style={{ background: checked ? 'var(--sv-gold-primary)' : 'var(--surface-3)', border: '1px solid var(--border)', padding: '0 2px' }}>
            <div className="w-4 h-4 rounded-full transition-transform" style={{ background: checked ? 'var(--bg)' : 'var(--muted-2)', transform: checked ? 'translateX(16px)' : 'translateX(0)' }} />
        </button>
    );
}

function NotificationsTab() {
    return (
        <div>
            <div className="sv-card-label mb-4">Preferências de notificação</div>
            <SettingRow label="Alertas de preco" desc="Receber notificações quando preços baixarem">
                <ToggleSwitch checked={true} onChange={() => {}} />
            </SettingRow>
            <SettingRow label="Novas ofertas" desc="Notificar sobre ofertas nas categorias favoritas">
                <ToggleSwitch checked={true} onChange={() => {}} />
            </SettingRow>
            <SettingRow label="Campanhas" desc="Alertas de status de campanhas">
                <ToggleSwitch checked={false} onChange={() => {}} />
            </SettingRow>
            <SettingRow label="Email" desc="Receber resumo diário por email">
                <ToggleSwitch checked={false} onChange={() => {}} />
            </SettingRow>
        </div>
    );
}

function SecurityTab() {
    return (
        <div>
            <div className="sv-card-label mb-4">Seguranca da conta</div>
            <SettingRow label="Alterar senha" desc="Ultima alteracao: nunca">
                <button className="sv-btn-system">Alterar</button>
            </SettingRow>
            <SettingRow label="Autenticacao 2FA" desc="Adicionar camada extra de seguranca">
                <ToggleSwitch checked={false} onChange={() => {}} />
            </SettingRow>
            <SettingRow label="Sessoes ativas" desc="Gerenciar dispositivos conectados">
                <button className="sv-btn-system">Ver sessoes</button>
            </SettingRow>
        </div>
    );
}

function SystemTab() {
    return (
        <div>
            <div className="sv-card-label mb-4">Sistema</div>
            <SettingRow label="Versao" desc="SuperVitrine Sistema">
                <span className="sv-badge sv-badge-gold">v1.0.0</span>
            </SettingRow>
            <SettingRow label="Cache" desc="Limpar dados em cache">
                <button className="sv-btn-system">Limpar cache</button>
            </SettingRow>
            <SettingRow label="Dados" desc="Exportar todos os dados do sistema">
                <button className="sv-btn-system">Exportar</button>
            </SettingRow>
        </div>
    );
}

function IntegrationsTab() {
    const [emailConfig, setEmailConfig] = useState({
        smtp_host: '', smtp_port: 587, smtp_user: '', smtp_password: '',
        from_email: '', from_name: 'SuperVitrine', use_tls: true, enabled: false
    });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [testing, setTesting] = useState(false);
    const [testResult, setTestResult] = useState(null);
    const token = localStorage.getItem('token');
    const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

    useEffect(() => {
        fetch(`${API}/email-config`, { headers })
            .then(r => r.json())
            .then(data => { setEmailConfig(prev => ({ ...prev, ...data })); })
            .catch(() => {})
            .finally(() => setLoading(false));
    }, []);

    const handleSave = async () => {
        setSaving(true);
        setTestResult(null);
        try {
            const res = await fetch(`${API}/email-config`, { method: 'POST', headers, body: JSON.stringify(emailConfig) });
            if (res.ok) {
                const data = await res.json();
                setEmailConfig(prev => ({ ...prev, ...data.config }));
                toast.success('Configuração de email salva!');
            } else toast.error('Erro ao salvar');
        } catch { toast.error('Erro de conexão'); }
        finally { setSaving(false); }
    };

    const handleTest = async () => {
        setTesting(true);
        setTestResult(null);
        try {
            const res = await fetch(`${API}/email-config/test`, { method: 'POST', headers });
            const data = await res.json();
            setTestResult(data);
            if (data.success) toast.success(data.message);
            else toast.error(data.message);
        } catch { toast.error('Erro ao testar'); setTestResult({ success: false, message: 'Erro de conexão' }); }
        finally { setTesting(false); }
    };

    const updateField = (key, value) => setEmailConfig(prev => ({ ...prev, [key]: value }));

    if (loading) return <div className="flex items-center justify-center py-8"><Loader2 size={20} className="animate-spin" style={{ color: 'var(--sv-gold-primary)' }} /></div>;

    return (
        <div>
            <div className="sv-card-label mb-4">Configuração de Email (SMTP)</div>

            {/* Email enabled toggle */}
            <SettingRow label="Envio de email ativo" desc="Ativar/desativar envio real de emails pelo sistema">
                <ToggleSwitch checked={emailConfig.enabled} onChange={() => updateField('enabled', !emailConfig.enabled)} />
            </SettingRow>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4">
                <EmailField label="Servidor SMTP" value={emailConfig.smtp_host} onChange={v => updateField('smtp_host', v)} placeholder="smtp.gmail.com" testid="email-smtp-host" />
                <EmailField label="Porta" value={emailConfig.smtp_port} onChange={v => updateField('smtp_port', parseInt(v) || 587)} placeholder="587" type="number" testid="email-smtp-port" />
                <EmailField label="Usuario SMTP" value={emailConfig.smtp_user} onChange={v => updateField('smtp_user', v)} placeholder="seu@email.com" testid="email-smtp-user" />
                <EmailField label="Senha SMTP" value={emailConfig.smtp_password} onChange={v => updateField('smtp_password', v)} placeholder="Senha ou App Password" type="password" testid="email-smtp-password" />
                <EmailField label="Email remetente" value={emailConfig.from_email} onChange={v => updateField('from_email', v)} placeholder="noreply@suavitrine.com" testid="email-from-email" />
                <EmailField label="Nome remetente" value={emailConfig.from_name} onChange={v => updateField('from_name', v)} placeholder="SuperVitrine" testid="email-from-name" />
            </div>

            <SettingRow label="Usar TLS" desc="Conexao segura (recomendado)">
                <ToggleSwitch checked={emailConfig.use_tls} onChange={() => updateField('use_tls', !emailConfig.use_tls)} />
            </SettingRow>

            {/* Test result */}
            {testResult && (
                <div className="mt-3 p-3 rounded-lg flex items-center gap-2 text-xs" style={{
                    background: testResult.success ? 'rgba(34,197,94,0.1)' : 'rgba(239,68,68,0.1)',
                    border: `1px solid ${testResult.success ? 'rgba(34,197,94,0.3)' : 'rgba(239,68,68,0.3)'}`,
                    color: testResult.success ? '#22c55e' : '#ef4444'
                }} data-testid="email-test-result">
                    {testResult.success ? <CheckCircle size={14} /> : <XCircle size={14} />}
                    {testResult.message}
                </div>
            )}

            {/* Action buttons */}
            <div className="flex gap-2 mt-4">
                <button className="sv-btn-gold-system sv-btn-system" onClick={handleSave} disabled={saving} data-testid="email-save-btn">
                    {saving ? <Loader2 size={14} className="animate-spin" /> : <Mail size={14} />}
                    <span className="ml-1.5">{saving ? 'Salvando...' : 'Salvar'}</span>
                </button>
                <button className="sv-btn-system" onClick={handleTest} disabled={testing || !emailConfig.smtp_host} data-testid="email-test-btn">
                    {testing ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle size={14} />}
                    <span className="ml-1.5">{testing ? 'Testando...' : 'Testar envio'}</span>
                </button>
            </div>

            {/* Auto-publish */}
            <div className="sv-card-label mb-4 mt-6">Auto-publicação no Site</div>
            <AutoPublishSection />

            {/* Telegram Notifications */}
            <div className="sv-card-label mb-4 mt-6">Notificações Telegram</div>
            <TelegramNotifySection />

            {/* Other integrations status */}
            <div className="sv-card-label mb-4 mt-6">Outras integrações</div>
            <a href="/sistema/admin/master?tab=integracoes&group=Telegram" className="block">
                <SettingRow label="Telegram Bot" desc="Bot principal conectado">
                    <span className="sv-badge sv-badge-success">Conectado →</span>
                </SettingRow>
            </a>
            <a href="/sistema/copilot" className="block">
                <SettingRow label="OpenAI API" desc="Copilot IA">
                    <span className="sv-badge sv-badge-success">Ativo →</span>
                </SettingRow>
            </a>
            <a href="/sistema/login-social" className="block">
                <SettingRow label="Google OAuth" desc="Login social">
                    <span className="sv-badge sv-badge-success">Ativo →</span>
                </SettingRow>
            </a>
        </div>
    );
}

const CRAWLER_SOURCE_INFO = {
    promobit: { label: 'Promobit (Multi-loja)', color: '#7C3AED' },
    kabum: { label: 'KaBuM', color: '#FF6500' },
    mercadolivre: { label: 'Mercado Livre', color: '#FFE600' },
    amazon: { label: 'Amazon BR', color: '#FF9900' },
    magalu: { label: 'Magazine Luiza', color: '#0086FF' },
    americanas: { label: 'Americanas', color: '#E60014' },
};

function CrawlerConfigTab() {
    const [config, setConfig] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [crawling, setCrawling] = useState(false);
    const [stats, setStats] = useState(null);
    const [newKw, setNewKw] = useState('');
    const [newBlock, setNewBlock] = useState('');
    const [newQuery, setNewQuery] = useState('');
    const token = localStorage.getItem('token');
    const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

    useEffect(() => {
        Promise.all([
            fetch(`${API}/crawler/config`, { headers }).then(r => r.json()),
            fetch(`${API}/crawler/stats`, { headers }).then(r => r.json()),
        ]).then(([cfg, st]) => { setConfig(cfg); setStats(st); })
        .catch(() => toast.error('Erro ao carregar config'))
        .finally(() => setLoading(false));
    }, []);

    const handleSave = async () => {
        setSaving(true);
        try {
            const r = await fetch(`${API}/crawler/config`, { method: 'POST', headers, body: JSON.stringify(config) });
            if (r.ok) { const d = await r.json(); setConfig(d.config); toast.success('Configuração salva!'); }
            else toast.error('Erro ao salvar');
        } catch { toast.error('Erro de conexão'); }
        finally { setSaving(false); }
    };

    const handleCrawl = async (src = 'all') => {
        setCrawling(true);
        try {
            const r = await fetch(`${API}/crawler/run`, { method: 'POST', headers, body: JSON.stringify({ source: src }) });
            const d = await r.json();
            toast.success(d.message || 'Concluido');
            const st = await fetch(`${API}/crawler/stats`, { headers }).then(r => r.json());
            setStats(st);
        } catch { toast.error('Erro ao rastrear'); }
        finally { setCrawling(false); }
    };

    const uf = (path, val) => {
        setConfig(prev => {
            const c = JSON.parse(JSON.stringify(prev));
            const keys = path.split('.');
            let ref = c;
            for (let i = 0; i < keys.length - 1; i++) ref = ref[keys[i]];
            ref[keys[keys.length - 1]] = val;
            return c;
        });
    };

    const addToList = (path, value, setter) => {
        if (!value.trim()) return;
        const keys = path.split('.');
        let ref = config;
        for (const k of keys) ref = ref[k];
        if (!ref.includes(value.trim())) {
            uf(path, [...ref, value.trim()]);
        }
        setter('');
    };

    const removeFromList = (path, idx) => {
        const keys = path.split('.');
        let ref = config;
        for (const k of keys) ref = ref[k];
        uf(path, ref.filter((_, i) => i !== idx));
    };

    if (loading || !config) return <div className="flex justify-center py-8"><Loader2 size={20} className="animate-spin" style={{ color: 'var(--sv-gold-primary)' }} /></div>;

    const filters = config.filters || {};

    return (
        <div>
            <div className="flex items-center justify-between mb-2">
                <div className="sv-card-label">Configuração do Crawler</div>
                <button className="sv-btn-gold-system sv-btn-system" onClick={() => handleCrawl('all')} disabled={crawling} data-testid="config-crawl-btn">
                    {crawling ? <Loader2 size={14} className="animate-spin" /> : <Play size={14} />}
                    <span className="ml-1">{crawling ? 'Rastreando...' : 'Rastrear agora'}</span>
                </button>
            </div>

            {/* Auto crawl toggle */}
            <SettingRow label="Rastreamento automático" desc="Busca ofertas periodicamente em segundo plano">
                <ToggleSwitch checked={config.auto_crawl_enabled} onChange={() => uf('auto_crawl_enabled', !config.auto_crawl_enabled)} />
            </SettingRow>

            {/* Sources */}
            <div className="sv-card-label mb-2 mt-4">Fontes</div>
            <div className="flex flex-col gap-2">
                {Object.entries(config.sources || {}).map(([key, src]) => {
                    const info = CRAWLER_SOURCE_INFO[key] || { label: key, color: '#888' };
                    return (
                        <div key={key} className="sv-card-system flex items-center gap-3" style={{ padding: '10px 14px' }} data-testid={`src-${key}`}>
                            <div className="w-3 h-3 rounded-full shrink-0" style={{ background: info.color }} />
                            <div className="flex-1">
                                <span className="text-sm font-medium" style={{ color: 'var(--text)' }}>{info.label}</span>
                                {src.last_run && <span className="text-[10px] ml-2" style={{ color: 'var(--muted-2)' }}>Ultimo: {new Date(src.last_run).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}</span>}
                                {stats?.by_source?.[key] > 0 && <span className="text-[10px] ml-2" style={{ color: 'var(--sv-gold-primary)' }}>{stats.by_source[key]} encontradas</span>}
                            </div>
                            <select value={src.interval_minutes} onChange={e => uf(`sources.${key}.interval_minutes`, parseInt(e.target.value))}
                                className="text-xs px-2 py-1 rounded-lg" style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text)', width: 80 }}>
                                <option value="15">15 min</option><option value="30">30 min</option><option value="45">45 min</option><option value="60">60 min</option><option value="120">2h</option>
                            </select>
                            <ToggleSwitch checked={src.enabled} onChange={() => uf(`sources.${key}.enabled`, !src.enabled)} />
                            <button className="sv-btn-system" style={{ padding: '4px 8px', fontSize: 10 }} onClick={() => handleCrawl(key)} disabled={crawling || !src.enabled}>
                                <RefreshCw size={11} />
                            </button>
                        </div>
                    );
                })}
            </div>

            {/* Search queries */}
            <div className="sv-card-label mb-2 mt-4">Termos de busca</div>
            <p className="text-[10px] mb-2" style={{ color: 'var(--muted-2)' }}>Palavras usadas para buscar ofertas. Pedidos de oferta dos usuários são adicionados automaticamente.</p>
            <div className="flex flex-wrap gap-1 mb-2">
                {(config.search_queries || []).map((q, i) => (
                    <span key={i} className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full" style={{ background: 'var(--surface-2)', color: 'var(--text)', border: '1px solid var(--border)' }}>
                        {q} <button onClick={() => removeFromList('search_queries', i)}><XCircle size={10} style={{ color: 'var(--muted-2)' }} /></button>
                    </span>
                ))}
            </div>
            <div className="flex gap-1">
                <input value={newQuery} onChange={e => setNewQuery(e.target.value)} placeholder="Novo termo..." onKeyDown={e => e.key === 'Enter' && addToList('search_queries', newQuery, setNewQuery)}
                    className="flex-1 px-2 py-1.5 rounded-lg text-xs" style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text)' }} data-testid="add-query" />
                <button className="sv-btn-system text-xs" style={{ padding: '4px 10px' }} onClick={() => addToList('search_queries', newQuery, setNewQuery)}><Plus size={12} /></button>
            </div>

            {/* Filters */}
            <div className="sv-card-label mb-2 mt-4">Filtros automáticos</div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-3">
                <div>
                    <label className="block text-xs font-medium mb-1" style={{ color: 'var(--muted)' }}>Desconto minimo (%)</label>
                    <input type="number" min="0" max="99" value={filters.min_discount || 0} onChange={e => uf('filters.min_discount', parseInt(e.target.value) || 0)}
                        className="w-full px-2 py-1.5 rounded-lg text-xs" style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text)' }} data-testid="filter-min-disc" />
                </div>
                <div>
                    <label className="block text-xs font-medium mb-1" style={{ color: 'var(--muted)' }}>Preco minimo (R$)</label>
                    <input type="number" min="0" value={filters.min_price || 0} onChange={e => uf('filters.min_price', parseFloat(e.target.value) || 0)}
                        className="w-full px-2 py-1.5 rounded-lg text-xs" style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text)' }} data-testid="filter-min-price" />
                </div>
                <div>
                    <label className="block text-xs font-medium mb-1" style={{ color: 'var(--muted)' }}>Preco maximo (R$)</label>
                    <input type="number" min="0" value={filters.max_price || 10000} onChange={e => uf('filters.max_price', parseFloat(e.target.value) || 10000)}
                        className="w-full px-2 py-1.5 rounded-lg text-xs" style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text)' }} data-testid="filter-max-price" />
                </div>
            </div>
            <div className="flex flex-col gap-2 mb-3">
                <SettingRow label="Exigir imagem" desc="Ignorar ofertas sem imagem"><ToggleSwitch checked={filters.require_image} onChange={() => uf('filters.require_image', !filters.require_image)} /></SettingRow>
                <SettingRow label="Exigir cupom" desc="Apenas ofertas com cupom"><ToggleSwitch checked={filters.require_coupon} onChange={() => uf('filters.require_coupon', !filters.require_coupon)} /></SettingRow>
                <SettingRow label="Exigir frete grátis" desc="Apenas ofertas com frete grátis"><ToggleSwitch checked={filters.require_free_shipping} onChange={() => uf('filters.require_free_shipping', !filters.require_free_shipping)} /></SettingRow>
            </div>

            {/* Priority keywords */}
            <div className="sv-card-label mb-2 mt-4">Palavras-chave prioritarias</div>
            <div className="flex flex-wrap gap-1 mb-2">
                {(filters.priority_keywords || []).map((kw, i) => (
                    <span key={i} className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full" style={{ background: 'rgba(var(--sv-accent-rgb), 0.1)', color: 'var(--sv-gold-primary)', border: '1px solid rgba(var(--sv-accent-rgb), 0.2)' }}>
                        {kw} <button onClick={() => removeFromList('filters.priority_keywords', i)}><XCircle size={10} /></button>
                    </span>
                ))}
            </div>
            <div className="flex gap-1">
                <input value={newKw} onChange={e => setNewKw(e.target.value)} placeholder="Ex: iphone, samsung..." onKeyDown={e => e.key === 'Enter' && addToList('filters.priority_keywords', newKw, setNewKw)}
                    className="flex-1 px-2 py-1.5 rounded-lg text-xs" style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text)' }} data-testid="add-kw" />
                <button className="sv-btn-system text-xs" style={{ padding: '4px 10px' }} onClick={() => addToList('filters.priority_keywords', newKw, setNewKw)}><Plus size={12} /></button>
            </div>

            {/* Blocked keywords */}
            <div className="sv-card-label mb-2 mt-4">Palavras bloqueadas</div>
            <div className="flex flex-wrap gap-1 mb-2">
                {(filters.blocked_keywords || []).map((bw, i) => (
                    <span key={i} className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full" style={{ background: 'rgba(239,68,68,0.1)', color: '#ef4444', border: '1px solid rgba(239,68,68,0.2)' }}>
                        {bw} <button onClick={() => removeFromList('filters.blocked_keywords', i)}><XCircle size={10} /></button>
                    </span>
                ))}
            </div>
            <div className="flex gap-1">
                <input value={newBlock} onChange={e => setNewBlock(e.target.value)} placeholder="Ex: usado, defeito..." onKeyDown={e => e.key === 'Enter' && addToList('filters.blocked_keywords', newBlock, setNewBlock)}
                    className="flex-1 px-2 py-1.5 rounded-lg text-xs" style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text)' }} data-testid="add-block" />
                <button className="sv-btn-system text-xs" style={{ padding: '4px 10px' }} onClick={() => addToList('filters.blocked_keywords', newBlock, setNewBlock)}><Plus size={12} /></button>
            </div>

            {/* Save */}
            <div className="flex gap-2 mt-5">
                <button className="sv-btn-gold-system sv-btn-system" onClick={handleSave} disabled={saving} data-testid="crawler-save-btn">
                    {saving ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle size={14} />}
                    <span className="ml-1">{saving ? 'Salvando...' : 'Salvar configuração'}</span>
                </button>
            </div>
        </div>
    );
}

const CONDITION_OPTIONS = [
    { value: 'always', label: 'Sempre', desc: 'Exibir em todas as ofertas' },
    { value: 'coupon_only', label: 'Somente com cupom', desc: 'Exibir apenas quando a oferta tiver cupom' },
];

function TelegramButtonsTab() {
    const [buttons, setButtons] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [editing, setEditing] = useState(null);
    const [saving, setSaving] = useState(false);
    const [form, setForm] = useState({ name: '', text: '', url: '', order: 0, active: true, condition: 'always' });
    const token = localStorage.getItem('token');
    const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

    useEffect(() => { fetchAll(); }, []);
    const fetchAll = () => {
        fetch(`${API}/telegram-buttons`, { headers })
            .then(r => r.json()).then(d => { if (Array.isArray(d)) setButtons(d); }).catch(() => {})
            .finally(() => setLoading(false));
    };

    const resetForm = () => { setForm({ name: '', text: '', url: '', order: 0, active: true, condition: 'always' }); setEditing(null); setShowForm(false); };

    const openEdit = (b) => {
        setForm({ name: b.name, text: b.text, url: b.url || '', order: b.order || 0, active: b.active, condition: b.condition || 'always' });
        setEditing(b.btn_id); setShowForm(true);
    };

    const handleSave = async () => {
        if (!form.name.trim() || !form.text.trim()) { toast.error('Nome e texto do botao obrigatorios'); return; }
        setSaving(true);
        try {
            const endpoint = editing ? `${API}/telegram-buttons/${editing}` : `${API}/telegram-buttons`;
            const method = editing ? 'PUT' : 'POST';
            const res = await fetch(endpoint, { method, headers, body: JSON.stringify(form) });
            if (res.ok) { toast.success(editing ? 'Botao atualizado!' : 'Botao criado!'); resetForm(); fetchAll(); }
            else toast.error('Erro ao salvar');
        } catch { toast.error('Erro de conexão'); }
        finally { setSaving(false); }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Excluir este botao?')) return;
        await fetch(`${API}/telegram-buttons/${id}`, { method: 'DELETE', headers });
        toast.success('Botao excluido'); fetchAll();
    };

    const toggleActive = async (b) => {
        await fetch(`${API}/telegram-buttons/${b.btn_id}`, { method: 'PUT', headers, body: JSON.stringify({ active: !b.active }) });
        fetchAll();
    };

    const moveOrder = async (b, direction) => {
        const newOrder = direction === 'up' ? Math.max(0, b.order - 1) : b.order + 1;
        await fetch(`${API}/telegram-buttons/${b.btn_id}`, { method: 'PUT', headers, body: JSON.stringify({ order: newOrder }) });
        fetchAll();
    };

    const uf = (k, v) => setForm(p => ({ ...p, [k]: v }));

    if (loading) return <div className="flex items-center justify-center py-8"><Loader2 size={20} className="animate-spin" style={{ color: 'var(--sv-gold-primary)' }} /></div>;

    return (
        <div>
            <div className="flex items-center justify-between mb-2">
                <div className="sv-card-label">Botões Inline do Telegram</div>
                <button className="sv-btn-gold-system sv-btn-system" onClick={() => { resetForm(); setShowForm(true); }} data-testid="new-tg-btn-btn">
                    <Plus size={14} /> <span className="ml-1">Novo botao</span>
                </button>
            </div>
            <p className="text-xs mb-4" style={{ color: 'var(--muted)' }}>
                Gerencie os botões que aparecem nas mensagens do Telegram. Use <code style={{ background: 'var(--surface-2)', padding: '1px 4px', borderRadius: 4 }}>{'{link}'}</code> na URL para inserir o link de afiliado da oferta. Condições permitem exibir botões apenas quando a oferta tiver cupom.
            </p>

            {showForm && (
                <div className="sv-card-system mb-4" data-testid="tg-btn-form">
                    <div className="sv-card-label mb-3">{editing ? 'Editar Botao' : 'Novo Botao'}</div>
                    <div className="flex flex-col gap-3">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <EmailField label="Nome interno *" value={form.name} onChange={v => uf('name', v)} placeholder="Ex: Ver oferta" testid="tg-btn-name" />
                            <EmailField label="Texto do botao *" value={form.text} onChange={v => uf('text', v)} placeholder="Texto visivel no Telegram" testid="tg-btn-text" />
                        </div>
                        <EmailField label="URL" value={form.url} onChange={v => uf('url', v)} placeholder="https://... ou {link} para link afiliado" testid="tg-btn-url" />
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                            <div>
                                <label className="block text-xs font-medium mb-1" style={{ color: 'var(--muted)' }}>Condicao</label>
                                <select value={form.condition} onChange={e => uf('condition', e.target.value)}
                                    className="w-full px-3 py-2 rounded-lg text-sm"
                                    style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text)', outline: 'none' }}
                                    data-testid="tg-btn-condition">
                                    {CONDITION_OPTIONS.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                                </select>
                                <p className="text-[10px] mt-1" style={{ color: 'var(--muted-2)' }}>{CONDITION_OPTIONS.find(c => c.value === form.condition)?.desc}</p>
                            </div>
                            <div>
                                <label className="block text-xs font-medium mb-1" style={{ color: 'var(--muted)' }}>Ordem</label>
                                <input type="number" min="0" value={form.order} onChange={e => uf('order', parseInt(e.target.value) || 0)}
                                    className="w-full px-3 py-2 rounded-lg text-sm"
                                    style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text)', outline: 'none' }}
                                    data-testid="tg-btn-order" />
                            </div>
                            <SettingRow label="Ativo" desc="Exibir nas mensagens">
                                <ToggleSwitch checked={form.active} onChange={() => uf('active', !form.active)} />
                            </SettingRow>
                        </div>
                        <div className="flex gap-2 pt-2">
                            <button className="sv-btn-gold-system sv-btn-system" onClick={handleSave} disabled={saving} data-testid="tg-btn-save">
                                {saving ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle size={14} />}
                                <span className="ml-1">{saving ? 'Salvando...' : 'Salvar'}</span>
                            </button>
                            <button className="sv-btn-system" onClick={resetForm}><XCircle size={14} /> <span className="ml-1">Cancelar</span></button>
                        </div>
                    </div>
                </div>
            )}

            {buttons.length === 0 && !showForm ? (
                <div className="sv-empty-state">
                    <Bot size={40} style={{ color: 'var(--muted-2)' }} />
                    <p className="sv-empty-state-title">Nenhum botao configurado</p>
                    <p className="sv-empty-state-desc">Crie botões para aparecer nas mensagens do Telegram</p>
                </div>
            ) : (
                <div className="flex flex-col gap-2" data-testid="tg-btn-list">
                    {buttons.map(b => (
                        <div key={b.btn_id} className="sv-card-system flex items-center gap-3" style={{ padding: '10px 14px', opacity: b.active ? 1 : 0.5 }}>
                            <div className="flex flex-col gap-0.5 shrink-0">
                                <button className="p-0.5 rounded hover:bg-[var(--surface-2)]" onClick={() => moveOrder(b, 'up')} data-testid={`tg-btn-up-${b.btn_id}`}><ArrowUp size={11} style={{ color: 'var(--muted)' }} /></button>
                                <button className="p-0.5 rounded hover:bg-[var(--surface-2)]" onClick={() => moveOrder(b, 'down')} data-testid={`tg-btn-down-${b.btn_id}`}><ArrowDown size={11} style={{ color: 'var(--muted)' }} /></button>
                            </div>
                            <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ background: 'rgba(38,165,228,0.12)' }}>
                                <Bot size={16} style={{ color: '#26A5E4' }} />
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                    <span className="text-sm font-medium truncate" style={{ color: 'var(--text)' }}>{b.text || b.name}</span>
                                    <span className={`sv-badge ${b.active ? 'sv-badge-success' : 'sv-badge-muted'}`} style={{ fontSize: 9 }}>{b.active ? 'Ativo' : 'Inativo'}</span>
                                    {b.condition === 'coupon_only' && <span className="text-[10px] px-1.5 py-0.5 rounded-full" style={{ background: 'rgba(var(--sv-accent-rgb), 0.1)', color: 'var(--sv-gold-primary)' }}>Cupom</span>}
                                    {(b.click_count || 0) > 0 && <span className="text-[10px] px-1.5 py-0.5 rounded-full" style={{ background: 'rgba(34,197,94,0.1)', color: '#22c55e' }}>{b.click_count} cliques</span>}
                                </div>
                                <p className="text-[10px] truncate" style={{ color: 'var(--muted-2)' }}>{b.url || '(sem URL)'}</p>
                            </div>
                            <span className="text-[10px] shrink-0" style={{ color: 'var(--muted-2)' }}>#{b.order}</span>
                            <div className="flex items-center gap-1 shrink-0">
                                <button className="sv-btn-system" style={{ padding: '4px 6px' }} onClick={() => toggleActive(b)} data-testid={`tg-btn-toggle-${b.btn_id}`}>
                                    {b.active ? <EyeOff size={12} /> : <Eye size={12} />}
                                </button>
                                <button className="sv-btn-system" style={{ padding: '4px 6px' }} onClick={() => openEdit(b)} data-testid={`tg-btn-edit-${b.btn_id}`}>
                                    <Settings size={12} />
                                </button>
                                <button className="sv-btn-system" style={{ padding: '4px 6px' }} onClick={() => handleDelete(b.btn_id)} data-testid={`tg-btn-del-${b.btn_id}`}>
                                    <Trash2 size={12} style={{ color: '#ef4444' }} />
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Preview info */}
            {buttons.filter(b => b.active).length > 0 && (
                <div className="mt-4 p-3 rounded-lg text-xs" style={{ background: 'rgba(38,165,228,0.06)', border: '1px solid rgba(38,165,228,0.15)', color: 'var(--muted)' }} data-testid="tg-btn-preview">
                    <span className="font-medium" style={{ color: '#26A5E4' }}>Preview:</span> {buttons.filter(b => b.active).length} botao(es) ativo(s) serao enviados nas mensagens do Telegram.
                    {buttons.filter(b => b.active && b.condition === 'coupon_only').length > 0 && (
                        <span> ({buttons.filter(b => b.active && b.condition === 'coupon_only').length} condicional)</span>
                    )}
                </div>
            )}
        </div>
    );
}


const PLATFORM_TYPES = [
    { value: 'telegram', label: 'Telegram', color: '#26A5E4' },
    { value: 'whatsapp', label: 'WhatsApp', color: '#25D366' },
    { value: 'instagram', label: 'Instagram', color: '#E4405F' },
    { value: 'discord', label: 'Discord', color: '#5865F2' },
    { value: 'youtube', label: 'YouTube', color: '#FF0000' },
    { value: 'site', label: 'Site', color: 'var(--sv-gold-primary)' },
    { value: 'outro', label: 'Outro', color: '#888' },
];

function PlatformLinksTab() {
    const [links, setLinks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [editing, setEditing] = useState(null);
    const [saving, setSaving] = useState(false);
    const [form, setForm] = useState({ name: '', url: '', platform_type: 'telegram', button_text: 'Entrar', order: 0, enabled: true, redirect_delay: 0 });
    const token = localStorage.getItem('token');
    const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

    useEffect(() => { fetchAll(); }, []);
    const fetchAll = () => {
        fetch(`${API}/platform-links`, { headers })
            .then(r => r.json()).then(d => { if (Array.isArray(d)) setLinks(d); }).catch(() => {})
            .finally(() => setLoading(false));
    };

    const resetForm = () => { setForm({ name: '', url: '', platform_type: 'telegram', button_text: 'Entrar', order: 0, enabled: true, redirect_delay: 0 }); setEditing(null); setShowForm(false); };

    const openEdit = (l) => {
        setForm({ name: l.name, url: l.url, platform_type: l.platform_type || 'outro', button_text: l.button_text || 'Entrar', order: l.order || 0, enabled: l.enabled, redirect_delay: l.redirect_delay || 0 });
        setEditing(l.link_id); setShowForm(true);
    };

    const handleSave = async () => {
        if (!form.name.trim() || !form.url.trim()) { toast.error('Nome e URL obrigatorios'); return; }
        setSaving(true);
        try {
            const url = editing ? `${API}/platform-links/${editing}` : `${API}/platform-links`;
            const method = editing ? 'PUT' : 'POST';
            const res = await fetch(url, { method, headers, body: JSON.stringify(form) });
            if (res.ok) { toast.success(editing ? 'Atualizado!' : 'Plataforma adicionada!'); resetForm(); fetchAll(); }
            else toast.error('Erro ao salvar');
        } catch { toast.error('Erro de conexão'); }
        finally { setSaving(false); }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Excluir esta plataforma?')) return;
        await fetch(`${API}/platform-links/${id}`, { method: 'DELETE', headers });
        toast.success('Excluido'); fetchAll();
    };

    const toggleEnabled = async (l) => {
        await fetch(`${API}/platform-links/${l.link_id}`, { method: 'PUT', headers, body: JSON.stringify({ enabled: !l.enabled }) });
        fetchAll();
    };

    const uf = (k, v) => setForm(p => ({ ...p, [k]: v }));
    const getPlatform = (type) => PLATFORM_TYPES.find(p => p.value === type) || PLATFORM_TYPES[6];

    if (loading) return <div className="flex items-center justify-center py-8"><Loader2 size={20} className="animate-spin" style={{ color: 'var(--sv-gold-primary)' }} /></div>;

    return (
        <div>
            <div className="flex items-center justify-between mb-2">
                <div className="sv-card-label">Botões, Links e Plataformas</div>
                <button className="sv-btn-gold-system sv-btn-system" onClick={() => { resetForm(); setShowForm(true); }} data-testid="new-platform-btn">
                    <Plus size={14} /> <span className="ml-1">Nova</span>
                </button>
            </div>
            <p className="text-xs mb-4" style={{ color: 'var(--muted)' }}>Gerencie botões e links para Telegram, WhatsApp, Discord, Site e outras plataformas. Esses botões aparecem no popup de Grupos e podem ser usados pelo bot do Telegram.</p>

            {showForm && (
                <div className="sv-card-system mb-4" data-testid="platform-form">
                    <div className="sv-card-label mb-3">{editing ? 'Editar Plataforma' : 'Nova Plataforma'}</div>
                    <div className="flex flex-col gap-3">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <EmailField label="Nome *" value={form.name} onChange={v => uf('name', v)} placeholder="Ex: Grupo Ofertas Telegram" testid="pl-name" />
                            <EmailField label="URL *" value={form.url} onChange={v => uf('url', v)} placeholder="https://t.me/seugrupo" testid="pl-url" />
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                            <div>
                                <label className="block text-xs font-medium mb-1" style={{ color: 'var(--muted)' }}>Tipo</label>
                                <select value={form.platform_type} onChange={e => uf('platform_type', e.target.value)}
                                    className="w-full px-3 py-2 rounded-lg text-sm"
                                    style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text)', outline: 'none' }}
                                    data-testid="pl-type">
                                    {PLATFORM_TYPES.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                                </select>
                            </div>
                            <EmailField label="Texto do botao" value={form.button_text} onChange={v => uf('button_text', v)} placeholder="Entrar" testid="pl-btn-text" />
                            <div>
                                <label className="block text-xs font-medium mb-1" style={{ color: 'var(--muted)' }}>Ordem</label>
                                <input type="number" min="0" value={form.order} onChange={e => uf('order', parseInt(e.target.value) || 0)}
                                    className="w-full px-3 py-2 rounded-lg text-sm"
                                    style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text)', outline: 'none' }}
                                    data-testid="pl-order" />
                            </div>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <SettingRow label="Ativo" desc="Visivel no popup de Grupos e Telegram"><ToggleSwitch checked={form.enabled} onChange={() => uf('enabled', !form.enabled)} /></SettingRow>
                            <div>
                                <label className="block text-xs font-medium mb-1" style={{ color: 'var(--muted)' }}>Atraso de redirecionamento (seg)</label>
                                <input type="number" min="0" max="10" value={form.redirect_delay} onChange={e => uf('redirect_delay', parseInt(e.target.value) || 0)}
                                    className="w-full px-3 py-2 rounded-lg text-sm"
                                    style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text)', outline: 'none' }}
                                    data-testid="pl-redirect-delay" />
                                <p className="text-[10px] mt-1" style={{ color: 'var(--muted-2)' }}>0 = redirect instantaneo. 5 = 5 seg de espera (monetizacao)</p>
                            </div>
                        </div>
                        <div className="flex gap-2 pt-2">
                            <button className="sv-btn-gold-system sv-btn-system" onClick={handleSave} disabled={saving} data-testid="pl-save-btn">
                                {saving ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle size={14} />} <span className="ml-1">{saving ? 'Salvando...' : 'Salvar'}</span>
                            </button>
                            <button className="sv-btn-system" onClick={resetForm}><XCircle size={14} /> <span className="ml-1">Cancelar</span></button>
                        </div>
                    </div>
                </div>
            )}

            {links.length === 0 && !showForm ? (
                <div className="sv-empty-state">
                    <Globe size={40} style={{ color: 'var(--muted-2)' }} />
                    <p className="sv-empty-state-title">Nenhuma plataforma cadastrada</p>
                    <p className="sv-empty-state-desc">Adicione links dos seus grupos e plataformas de ofertas</p>
                </div>
            ) : (
                <div className="flex flex-col gap-2">
                    {links.map(l => {
                        const pt = getPlatform(l.platform_type);
                        return (
                            <div key={l.link_id} className="sv-card-system flex items-center gap-3" style={{ padding: '10px 14px' }}>
                                <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ background: `${pt.color}20` }}>
                                    <PlatformIcon name={pt.value} size={16} />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2">
                                        <span className="text-sm font-medium truncate" style={{ color: 'var(--text)' }}>{l.name}</span>
                                        <span className={`sv-badge ${l.enabled ? 'sv-badge-success' : 'sv-badge-muted'}`} style={{ fontSize: 9 }}>{l.enabled ? 'Ativo' : 'Inativo'}</span>
                                        {(l.click_count || 0) > 0 && <span className="text-[10px] px-1.5 py-0.5 rounded-full" style={{ background: 'rgba(var(--sv-accent-rgb), 0.1)', color: 'var(--sv-gold-primary)' }}>{l.click_count} cliques</span>}
                                        <span className="sv-badge sv-badge-gold" style={{ fontSize: 9 }}>{pt.label}</span>
                                    </div>
                                    <p className="text-[10px] truncate" style={{ color: 'var(--muted-2)' }}>{l.url}</p>
                                </div>
                                <span className="text-[10px] shrink-0" style={{ color: 'var(--muted-2)' }}>#{l.order}</span>
                                <div className="flex items-center gap-1 shrink-0">
                                    <button className="sv-btn-system" style={{ padding: '4px 6px' }} onClick={() => toggleEnabled(l)}>
                                        {l.enabled ? <XCircle size={12} /> : <CheckCircle size={12} />}
                                    </button>
                                    <button className="sv-btn-system" style={{ padding: '4px 6px' }} onClick={() => openEdit(l)} data-testid={`pl-edit-${l.link_id}`}>
                                        <Settings size={12} />
                                    </button>
                                    <button className="sv-btn-system" style={{ padding: '4px 6px' }} onClick={() => handleDelete(l.link_id)}>
                                        <Trash2 size={12} style={{ color: '#ef4444' }} />
                                    </button>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}


function AnnouncementsTab() {
    const [comunicados, setComunicados] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [editing, setEditing] = useState(null);
    const [saving, setSaving] = useState(false);
    const [form, setForm] = useState({ title: '', message: '', image_url: '', enabled: false, start_date: '', end_date: '', show_on_login: true, show_once: false, duration_seconds: 0 });
    const token = localStorage.getItem('token');
    const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

    useEffect(() => { fetchAll(); }, []);
    const fetchAll = () => {
        fetch(`${API}/comunicados`, { headers })
            .then(r => r.json()).then(d => { if (Array.isArray(d)) setComunicados(d); }).catch(() => {})
            .finally(() => setLoading(false));
    };

    const resetForm = () => { setForm({ title: '', message: '', image_url: '', enabled: false, start_date: '', end_date: '', show_on_login: true, show_once: false, duration_seconds: 0 }); setEditing(null); setShowForm(false); };

    const openEdit = (c) => {
        setForm({ title: c.title, message: c.message, image_url: c.image_url || '', enabled: c.enabled, start_date: c.start_date?.slice(0, 16) || '', end_date: c.end_date?.slice(0, 16) || '', show_on_login: c.show_on_login, show_once: c.show_once, duration_seconds: c.duration_seconds || 0 });
        setEditing(c.comunicado_id);
        setShowForm(true);
    };

    const handleSave = async () => {
        if (!form.title.trim() || !form.message.trim()) { toast.error('Titulo e mensagem obrigatorios'); return; }
        setSaving(true);
        const payload = { ...form, start_date: form.start_date ? new Date(form.start_date).toISOString() : new Date().toISOString(), end_date: form.end_date ? new Date(form.end_date).toISOString() : '' };
        try {
            const url = editing ? `${API}/comunicados/${editing}` : `${API}/comunicados`;
            const method = editing ? 'PUT' : 'POST';
            const res = await fetch(url, { method, headers, body: JSON.stringify(payload) });
            if (res.ok) { toast.success(editing ? 'Comunicado atualizado!' : 'Comunicado criado!'); resetForm(); fetchAll(); }
            else toast.error('Erro ao salvar');
        } catch { toast.error('Erro de conexão'); }
        finally { setSaving(false); }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Excluir este comunicado?')) return;
        await fetch(`${API}/comunicados/${id}`, { method: 'DELETE', headers });
        toast.success('Excluido'); fetchAll();
    };

    const toggleEnabled = async (c) => {
        await fetch(`${API}/comunicados/${c.comunicado_id}`, { method: 'PUT', headers, body: JSON.stringify({ enabled: !c.enabled }) });
        fetchAll();
    };

    const uf = (k, v) => setForm(p => ({ ...p, [k]: v }));

    if (loading) return <div className="flex items-center justify-center py-8"><Loader2 size={20} className="animate-spin" style={{ color: 'var(--sv-gold-primary)' }} /></div>;

    return (
        <div>
            <div className="flex items-center justify-between mb-4">
                <div className="sv-card-label">Comunicados do sistema</div>
                <button className="sv-btn-gold-system sv-btn-system" onClick={() => { resetForm(); setShowForm(true); }} data-testid="new-comunicado-btn">
                    <Megaphone size={14} /> <span className="ml-1">Novo</span>
                </button>
            </div>

            {/* Form */}
            {showForm && (
                <div className="sv-card-system mb-4" data-testid="comunicado-form">
                    <div className="sv-card-label mb-3">{editing ? 'Editar Comunicado' : 'Novo Comunicado'}</div>
                    <div className="flex flex-col gap-3">
                        <EmailField label="Titulo *" value={form.title} onChange={v => uf('title', v)} placeholder="Titulo do comunicado" testid="com-title" />
                        <div>
                            <label className="block text-xs font-medium mb-1" style={{ color: 'var(--muted)' }}>Mensagem *</label>
                            <textarea value={form.message} onChange={e => uf('message', e.target.value)} rows={4} placeholder="Texto do comunicado..."
                                className="w-full px-3 py-2 rounded-lg text-sm resize-none"
                                style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text)', outline: 'none' }}
                                data-testid="com-message" />
                        </div>
                        <EmailField label="URL da Imagem (opcional)" value={form.image_url} onChange={v => uf('image_url', v)} placeholder="https://..." testid="com-image" />
                        {form.image_url && <img src={form.image_url} alt="" className="max-h-24 rounded-lg object-contain" onError={e => e.target.style.display = 'none'} />}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div>
                                <label className="block text-xs font-medium mb-1" style={{ color: 'var(--muted)' }}>Inicio</label>
                                <input type="datetime-local" value={form.start_date} onChange={e => uf('start_date', e.target.value)}
                                    className="w-full px-3 py-2 rounded-lg text-sm" style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text)', outline: 'none' }} data-testid="com-start" />
                            </div>
                            <div>
                                <label className="block text-xs font-medium mb-1" style={{ color: 'var(--muted)' }}>Fim (opcional)</label>
                                <input type="datetime-local" value={form.end_date} onChange={e => uf('end_date', e.target.value)}
                                    className="w-full px-3 py-2 rounded-lg text-sm" style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text)', outline: 'none' }} data-testid="com-end" />
                            </div>
                        </div>
                        <SettingRow label="Ativo" desc="Comunicado visivel para usuarios"><ToggleSwitch checked={form.enabled} onChange={() => uf('enabled', !form.enabled)} /></SettingRow>
                        <SettingRow label="Exibir ao abrir o sistema" desc="Mostrar popup quando o usuario entrar"><ToggleSwitch checked={form.show_on_login} onChange={() => uf('show_on_login', !form.show_on_login)} /></SettingRow>
                        <SettingRow label="Mostrar apenas uma vez por usuario" desc="Depois de dispensar, não exibir novamente"><ToggleSwitch checked={form.show_once} onChange={() => uf('show_once', !form.show_once)} /></SettingRow>
                        <SettingRow label="Duracao do popup (segundos)" desc="0 = sem auto-fechar">
                            <input type="number" min="0" max="300" value={form.duration_seconds} onChange={e => uf('duration_seconds', parseInt(e.target.value) || 0)}
                                className="w-20 px-3 py-1.5 rounded-lg text-sm text-center"
                                style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text)', outline: 'none' }} data-testid="com-duration" />
                        </SettingRow>
                        <div className="flex gap-2 pt-2">
                            <button className="sv-btn-gold-system sv-btn-system" onClick={handleSave} disabled={saving} data-testid="com-save-btn">
                                {saving ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle size={14} />} <span className="ml-1">{saving ? 'Salvando...' : 'Salvar'}</span>
                            </button>
                            <button className="sv-btn-system" onClick={resetForm}><XCircle size={14} /> <span className="ml-1">Cancelar</span></button>
                        </div>
                    </div>
                </div>
            )}

            {/* List */}
            {comunicados.length === 0 && !showForm ? (
                <div className="sv-empty-state">
                    <Megaphone size={40} style={{ color: 'var(--muted-2)' }} />
                    <p className="sv-empty-state-title">Nenhum comunicado</p>
                    <p className="sv-empty-state-desc">Crie comunicados para seus usuarios do sistema</p>
                </div>
            ) : (
                <div className="flex flex-col gap-2">
                    {comunicados.map(c => (
                        <div key={c.comunicado_id} className="sv-card-system flex items-start gap-3" style={{ padding: '12px 16px' }}>
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-1">
                                    <span className="text-sm font-medium truncate" style={{ color: 'var(--text)' }}>{c.title}</span>
                                    <span className={`sv-badge ${c.enabled ? 'sv-badge-success' : 'sv-badge-muted'}`} style={{ fontSize: 9 }}>{c.enabled ? 'Ativo' : 'Inativo'}</span>
                                    {c.show_on_login && <span className="sv-badge sv-badge-gold" style={{ fontSize: 9 }}>Popup</span>}
                                </div>
                                <p className="text-xs truncate" style={{ color: 'var(--muted)' }}>{c.message?.slice(0, 80)}</p>
                                <p className="text-[10px] mt-1" style={{ color: 'var(--muted-2)' }}>
                                    {c.start_date ? new Date(c.start_date).toLocaleDateString('pt-BR') : ''} {c.end_date ? `ate ${new Date(c.end_date).toLocaleDateString('pt-BR')}` : ''}
                                </p>
                            </div>
                            <div className="flex items-center gap-1 shrink-0">
                                <button className="sv-btn-system" style={{ padding: '4px 6px' }} onClick={() => toggleEnabled(c)} title={c.enabled ? 'Desativar' : 'Ativar'}>
                                    {c.enabled ? <XCircle size={12} /> : <CheckCircle size={12} />}
                                </button>
                                <button className="sv-btn-system" style={{ padding: '4px 6px' }} onClick={() => openEdit(c)} data-testid={`com-edit-${c.comunicado_id}`}>
                                    <Settings size={12} />
                                </button>
                                <button className="sv-btn-system" style={{ padding: '4px 6px' }} onClick={() => handleDelete(c.comunicado_id)} data-testid={`com-del-${c.comunicado_id}`}>
                                    <XCircle size={12} style={{ color: '#ef4444' }} />
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

function AutoPublishSection() {
    const [config, setConfig] = useState({ auto_approve_enabled: false, min_discount: 0, total_published: 0 });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const token = localStorage.getItem('token');
    const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

    useEffect(() => {
        fetch(`${API}/publish-config`, { headers })
            .then(r => r.json()).then(d => setConfig(prev => ({ ...prev, ...d }))).catch(() => {})
            .finally(() => setLoading(false));
    }, []);

    const handleSave = async () => {
        setSaving(true);
        try {
            const res = await fetch(`${API}/publish-config`, { method: 'POST', headers, body: JSON.stringify(config) });
            if (res.ok) { const d = await res.json(); setConfig(prev => ({ ...prev, ...d.config })); toast.success('Auto-publicação configurada!'); }
            else toast.error('Erro ao salvar');
        } catch { toast.error('Erro de conexão'); }
        finally { setSaving(false); }
    };

    if (loading) return <div className="flex items-center justify-center py-4"><Loader2 size={16} className="animate-spin" style={{ color: 'var(--sv-gold-primary)' }} /></div>;

    return (
        <div>
            <SettingRow label="Auto-aprovar ofertas" desc="Ofertas de usuários aprovados são publicadas automaticamente no site">
                <ToggleSwitch checked={config.auto_approve_enabled} onChange={() => setConfig(p => ({ ...p, auto_approve_enabled: !p.auto_approve_enabled }))} />
            </SettingRow>
            {config.auto_approve_enabled && (
                <SettingRow label="Desconto mínimo (%)" desc="Ofertas com desconto abaixo desse valor ficam pendentes">
                    <input type="number" min="0" max="100" value={config.min_discount}
                        onChange={e => setConfig(p => ({ ...p, min_discount: parseInt(e.target.value) || 0 }))}
                        className="w-20 px-3 py-1.5 rounded-lg text-sm text-center"
                        style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text)', outline: 'none' }}
                        data-testid="publish-min-discount" />
                </SettingRow>
            )}
            <SettingRow label="Ofertas publicadas no site" desc="Total de ofertas visiveis na homepage">
                <span className="sv-badge sv-badge-gold">{config.total_published}</span>
            </SettingRow>
            <div className="mt-3">
                <button className="sv-btn-gold-system sv-btn-system" onClick={handleSave} disabled={saving} data-testid="publish-save-btn">
                    {saving ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle size={14} />}
                    <span className="ml-1.5">{saving ? 'Salvando...' : 'Salvar'}</span>
                </button>
            </div>
        </div>
    );
}

function EmailField({ label, value, onChange, placeholder, type = 'text', testid }) {
    return (
        <div>
            <label className="block text-xs font-medium mb-1" style={{ color: 'var(--muted)' }}>{label}</label>
            <input type={type} value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder}
                className="w-full px-3 py-2 rounded-lg text-sm"
                style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text)', outline: 'none' }}
                data-testid={testid} />
        </div>
    );
}

function TelegramNotifySection() {
    const [config, setConfig] = useState({
        enabled: false, min_discount: 40, chat_id: '',
        template: '', last_send_status: '', last_send_at: '', last_send_deal: ''
    });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [testing, setTesting] = useState(false);
    const [testResult, setTestResult] = useState(null);
    const token = localStorage.getItem('token');
    const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

    useEffect(() => {
        fetch(`${API}/telegram-notify-config`, { headers })
            .then(r => r.json()).then(d => setConfig(prev => ({ ...prev, ...d }))).catch(() => {})
            .finally(() => setLoading(false));
    }, []);

    const handleSave = async () => {
        setSaving(true);
        setTestResult(null);
        try {
            const res = await fetch(`${API}/telegram-notify-config`, { method: 'POST', headers, body: JSON.stringify(config) });
            if (res.ok) { const d = await res.json(); setConfig(prev => ({ ...prev, ...d.config })); toast.success('Configuração salva!'); }
            else toast.error('Erro ao salvar');
        } catch { toast.error('Erro de conexão'); }
        finally { setSaving(false); }
    };

    const handleTest = async () => {
        setTesting(true);
        setTestResult(null);
        try {
            const res = await fetch(`${API}/telegram-notify-config/test`, { method: 'POST', headers });
            const d = await res.json();
            setTestResult(d);
            if (d.success) toast.success(d.message); else toast.error(d.message || d.detail);
        } catch { toast.error('Erro ao testar'); setTestResult({ success: false, message: 'Erro de conexão' }); }
        finally { setTesting(false); }
    };

    const updateField = (k, v) => setConfig(prev => ({ ...prev, [k]: v }));

    if (loading) return <div className="flex items-center justify-center py-4"><Loader2 size={16} className="animate-spin" style={{ color: 'var(--sv-gold-primary)' }} /></div>;

    return (
        <div>
            <SettingRow label="Notificação automática" desc="Enviar no Telegram quando ofertas com desconto alto forem publicadas">
                <ToggleSwitch checked={config.enabled} onChange={() => updateField('enabled', !config.enabled)} />
            </SettingRow>

            {config.enabled && (
                <>
                    <SettingRow label="Desconto mínimo para notificar (%)" desc="Apenas ofertas com desconto acima desse valor">
                        <input type="number" min="0" max="100" value={config.min_discount}
                            onChange={e => updateField('min_discount', parseInt(e.target.value) || 0)}
                            className="w-20 px-3 py-1.5 rounded-lg text-sm text-center"
                            style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text)', outline: 'none' }}
                            data-testid="tg-notify-min-discount" />
                    </SettingRow>

                    <div className="py-3" style={{ borderBottom: '1px solid var(--border)' }}>
                        <label className="block text-xs font-medium mb-1.5" style={{ color: 'var(--muted)' }}>Destino (Chat ID, grupo ou canal)</label>
                        <input type="text" value={config.chat_id} onChange={e => updateField('chat_id', e.target.value)}
                            placeholder="Ex: -1001234567890 ou @nomedocanal"
                            className="w-full px-3 py-2 rounded-lg text-sm"
                            style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text)', outline: 'none' }}
                            data-testid="tg-notify-chat-id" />
                    </div>

                    <div className="py-3" style={{ borderBottom: '1px solid var(--border)' }}>
                        <label className="block text-xs font-medium mb-1.5" style={{ color: 'var(--muted)' }}>
                            Template da mensagem
                            <span className="ml-2 font-normal" style={{ color: 'var(--muted-2)' }}>Variaveis: {'{titulo}'} {'{preco}'} {'{preco_original}'} {'{desconto}'} {'{loja}'} {'{link}'} {'{cupom}'}</span>
                        </label>
                        <textarea value={config.template} onChange={e => updateField('template', e.target.value)} rows={5}
                            className="w-full px-3 py-2 rounded-lg text-sm resize-none font-mono"
                            style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text)', outline: 'none', fontSize: 11 }}
                            data-testid="tg-notify-template" />
                    </div>
                </>
            )}

            {/* Status */}
            {config.last_send_status && (
                <SettingRow label="Ultimo envio" desc={config.last_send_deal || ''}>
                    <div className="flex items-center gap-2">
                        <span className={`sv-badge ${config.last_send_status.startsWith('ok') ? 'sv-badge-success' : 'sv-badge-warning'}`}>
                            {config.last_send_status}
                        </span>
                        {config.last_send_at && <span className="text-[10px]" style={{ color: 'var(--muted-2)' }}>{new Date(config.last_send_at).toLocaleString('pt-BR')}</span>}
                    </div>
                </SettingRow>
            )}

            {/* Test result */}
            {testResult && (
                <div className="mt-2 p-3 rounded-lg flex items-center gap-2 text-xs" style={{
                    background: testResult.success ? 'rgba(34,197,94,0.1)' : 'rgba(239,68,68,0.1)',
                    border: `1px solid ${testResult.success ? 'rgba(34,197,94,0.3)' : 'rgba(239,68,68,0.3)'}`,
                    color: testResult.success ? '#22c55e' : '#ef4444'
                }} data-testid="tg-notify-test-result">
                    {testResult.success ? <CheckCircle size={14} /> : <XCircle size={14} />}
                    {testResult.message}
                </div>
            )}

            <div className="flex gap-2 mt-3">
                <button className="sv-btn-gold-system sv-btn-system" onClick={handleSave} disabled={saving} data-testid="tg-notify-save-btn">
                    {saving ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle size={14} />}
                    <span className="ml-1.5">{saving ? 'Salvando...' : 'Salvar'}</span>
                </button>
                <button className="sv-btn-system" onClick={handleTest} disabled={testing || !config.chat_id} data-testid="tg-notify-test-btn">
                    {testing ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                    <span className="ml-1.5">{testing ? 'Testando...' : 'Testar envio'}</span>
                </button>
            </div>
        </div>
    );
}
